/**
 * Tutorial Video Store — manages per-theme tutorial videos with timestamps.
 * Each storefront theme can have its own video with navigable chapters.
 * Config is synced via app_data with key khays_tutorial_video.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const KEY = "khays_tutorial_video";

export interface VideoTimestamp {
  id: string;
  time: number; // seconds
  label: string;
}

export interface ThemeVideoConfig {
  videoUrl: string;
  timestamps: VideoTimestamp[];
}

export type TimestampNavMode = "chapters" | "buttons" | "dropdown";

export interface TutorialVideoConfig {
  enabled: boolean;
  buttonText: string;
  buttonFontSize: number;
  buttonPosition: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  effect: "none" | "pulse" | "glow" | "bounce" | "shake";
  buttonColor: string;
  buttonTextColor: string;
  /** Per-theme videos: classic, modern, minimal */
  themeVideos: Record<string, ThemeVideoConfig>;
  /** Which timestamp navigation modes are active */
  timestampNavModes: TimestampNavMode[];
  /** Legacy single videoUrl — migrated to themeVideos on load */
  videoUrl?: string;
}

function getDefaults(): TutorialVideoConfig {
  return {
    enabled: false,
    buttonText: "📖 How to Navigate",
    buttonFontSize: 12,
    buttonPosition: "top-center",
    effect: "pulse",
    buttonColor: "",
    buttonTextColor: "",
    themeVideos: {},
    timestampNavModes: ["chapters"],
  };
}

function loadFromStorage(): TutorialVideoConfig {
  try {
    const s = localStorage.getItem(KEY);
    if (s) {
      const parsed = { ...getDefaults(), ...JSON.parse(s) };
      // Migrate legacy single videoUrl to themeVideos
      if (parsed.videoUrl && !Object.keys(parsed.themeVideos || {}).length) {
        parsed.themeVideos = { classic: { videoUrl: parsed.videoUrl, timestamps: [] } };
        delete parsed.videoUrl;
        localStorage.setItem(KEY, JSON.stringify(parsed));
      }
      if (!parsed.themeVideos) parsed.themeVideos = {};
      if (!parsed.timestampNavModes) parsed.timestampNavModes = ["chapters"];
      return parsed;
    }
  } catch {}
  return getDefaults();
}

interface TutorialVideoState extends TutorialVideoConfig {
  setConfig: (c: TutorialVideoConfig) => void;
  patch: (p: Partial<TutorialVideoConfig>) => void;
  reload: () => void;
}

export const useTutorialVideoStore = create<TutorialVideoState>()(
  devtools(
    (set, get) => ({
      ...loadFromStorage(),
      setConfig: (c) => {
        localStorage.setItem(KEY, JSON.stringify(c));
        set(c);
      },
      patch: (p) => {
        const { setConfig, patch, reload, ...current } = get();
        const next = { ...current, ...p };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(p);
      },
      reload: () => set(loadFromStorage()),
    }),
    { name: "TutorialVideoStore" }
  )
);

/** Get the video config for the currently active theme */
export function getVideoForTheme(theme: string): ThemeVideoConfig | null {
  const state = useTutorialVideoStore.getState();
  return state.themeVideos[theme] || null;
}
